import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Button, Collapse, ClickAwayListener } from "@mui/material";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { useNavigate } from "react-router-dom";
import { getProfile, getProgramStatus } from "../api/bot.api";
import { useDispatch, useSelector } from "react-redux";
import {
    setIsInUSAction,
    setProfileAction,
    setSettingsAction,
} from "../store/slice";
import { RootState } from "../store";
import { useWallet } from "@solana/wallet-adapter-react";
import { MenuInterface } from "./types";
import { checkIfIpIsInUS } from "../libs/utils";
import SnackMsg from "./SnackMsg";
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';

const initialProfile = {
    pubkey: "",
    isAdmin: false,
    balances: {},
    totalBalance: 0,
    nfts: [],
};

const Header = () => {
    const { wallet, connected, publicKey, connect } = useWallet();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const profile = useSelector((state: RootState) => state.profile);
    const selectedMenu = useSelector((state: RootState) => state.selectedMenu);
    const isWalletConnectedRef = React.useRef(false);
    const isInUS = useSelector((state: RootState) => state.isInUS);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const menuButtonRef = useRef<HTMLButtonElement>(null);

    const getProfileHandler = async (pubkey: string) => {
        const res = await getProfile({ pubkey: pubkey });
        if (res.success && isWalletConnectedRef.current) {
            dispatch(setProfileAction(res.data));
        }
    };

    const checkIpUS = async () => {
        const isInUS = await checkIfIpIsInUS();
        if (!isInUS) {
            dispatch(setIsInUSAction(false));
        } else {
            dispatch(setIsInUSAction(true));
        }
    };

    useEffect(() => {
        checkIpUS();
        const fetchProgramStatus = async () => {
            const res = await getProgramStatus();
            if (res.success && res.data) {
                dispatch(setSettingsAction(res.data));
            }
        };
        fetchProgramStatus();
        const interval = setInterval(() => {
            fetchProgramStatus();
        }, 20000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (wallet && !connected) {
            // If a wallet is selected and not connected, attempt to connect
            connect().catch((error) => {
                console.error("Failed to connect wallet:", error);
            });
        }
        if (connected && publicKey) {
            isWalletConnectedRef.current = true;
            getProfileHandler(publicKey.toBase58());
            const interval = setInterval(() => {
                getProfileHandler(publicKey.toBase58());
            }, 10000);
            return () => clearInterval(interval);
        } else {
            isWalletConnectedRef.current = false;
            dispatch(setProfileAction(initialProfile));
        }
    }, [wallet, connected, publicKey]);

    const handleMenuToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const MenuItems = () => (
        <>
            <Button component="a" href="https://smartypants.exchange" sx={{ width: { xs: '100%', md: 'auto' }, my: { xs: 1, md: 0 } }}>
                <Typography variant="button" textTransform="none" color="warning">Home</Typography>
            </Button>
            <Button 
                onClick={() => {
                    navigate("/");
                    setMobileMenuOpen(false);
                }} 
                sx={{ 
                    width: { xs: '100%', md: 'auto' },
                    my: { xs: 1, md: 0 },
                    ml: { md: 2 },
                    backgroundColor: selectedMenu === MenuInterface.HOME ? "rgba(144, 202, 249, 0.08)" : "transparent",
                }}
            >
                <Typography variant="button" textTransform="none" color="#ccffff">Buy/Sell</Typography>
            </Button>
            <Button 
                onClick={() => {
                    navigate("/portfolio");
                    setMobileMenuOpen(false);
                }} 
                sx={{ 
                    width: { xs: '100%', md: 'auto' },
                    my: { xs: 1, md: 0 },
                    ml: { md: 2 },
                    backgroundColor: selectedMenu === MenuInterface.PORTFOLIO ? "rgba(144, 202, 249, 0.08)" : "transparent",
                }}
            >
                <Typography variant="button" textTransform="none" color="#ccffff">Portfolio</Typography>
            </Button>
            {profile.isAdmin && (
                <Button 
                    onClick={() => {
                        navigate("/admin");
                        setMobileMenuOpen(false);
                    }} 
                    sx={{ 
                        width: { xs: '100%', md: 'auto' },
                        my: { xs: 1, md: 0 },
                        ml: { md: 2 },
                        backgroundColor: selectedMenu === MenuInterface.ADMIN ? "rgba(144, 202, 249, 0.08)" : "transparent",
                    }}
                >
                    <Typography variant="button" textTransform="none" color="#ccffff">Admin</Typography>
                </Button>
            )}
            {profile.isAdmin && (
                <Button 
                    onClick={() => {
                        navigate("/rewards2");
                        setMobileMenuOpen(false);
                    }} 
                    sx={{ 
                        width: { xs: '100%', md: 'auto' },
                        my: { xs: 1, md: 0 },
                        ml: { md: 2 },
                        backgroundColor: selectedMenu === MenuInterface.REWARD ? "rgba(144, 202, 249, 0.08)" : "transparent",
                    }}
                >
                    <Typography variant="button" textTransform="none" color="#ccffff">Rewards2</Typography>
                </Button>
            )}
        </>
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <SnackMsg />
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 30,
                    bgcolor: "background.default",
                }}
            >
                {!isInUS && (
                    <Typography variant="caption">
                        {"Welcome to Our Affiliate System."}
                    </Typography>
                )}
                {isInUS && (
                    <Typography variant="caption" color="warning">
                        {"Only Non-US Residents can signup!"}
                    </Typography>
                )}
            </Box>
            <Box
                sx={{
                    zIndex: 2,
                    width: "100%",
                    bgcolor: "background.header",
                    height: 60,
                    minHeight: 60,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderTop: "1px solid #1e1e1e",
                    borderBottom: "1px solid #1e1e1e",
                }}
                px={{xs: 2, sm: 2, md: 3}}
            >

                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flex={1}>
                    <Box sx={{ display: { xs: 'none', sm: 'block' }, width: 140, height: 50, borderRadius: 2, overflow: "hidden", ":hover": { cursor: "pointer" }}} onClick={() => navigate("/")}>
                        <img src={"../assets/logo.png"} alt="logo" style={{ width: "100%", height: "100%" }}/>
                    </Box>
                    <Box sx={{ display: { xs: 'block', sm: 'none' }, width: 50, height: 50, borderRadius: 2, overflow: "hidden", ":hover": { cursor: "pointer" }}} onClick={() => navigate("/")}>
                        <img src={"../assets/logo-small.png"} alt="logo" style={{ width: "100%", height: "100%" }}/>
                    </Box>
                    <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: { xs: "flex-end", md: "flex-start"} }}>
                        {/* Desktop Menu */}
                        <Box sx={{ display: { xs: 'none', md: 'flex' }}} alignItems="center" gap={2}>
                            <MenuItems />
                        </Box>

                        {/* Mobile Menu Icon and Title */}
                        <Box sx={{ 
                            display: { xs: 'flex', md: 'none' },
                            alignItems: 'center',
                            mr: 1
                        }}>
                            <IconButton
                                ref={menuButtonRef}
                                onClick={handleMenuToggle}
                                color="inherit"
                                sx={{ mr: { sm: 1 } }}
                            >
                                <MenuIcon sx={{ color: '#ccffff' }} />
                            </IconButton>
                            
                        </Box>
                    </Box>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Box
                        sx={{
                            "& .wallet-adapter-button-trigger": {
                                backgroundColor: "background.default",
                                color: "#90caf9",
                                border: "1px solid",
                                borderColor: "#90caf980",
                                height: 40,
                                fontWeight: 500,
                                transition: "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                                '&:hover': {
                                    borderColor: "#90caf9",
                                    backgroundColor: "#90caf914",
                                },
                            },
                        }}
                    >
                        <WalletMultiButton />
                    </Box>
                </Box>
            </Box>

            {/* Mobile Menu Bar */}
            <ClickAwayListener 
                onClickAway={(event) => {
                    if (menuButtonRef.current && !menuButtonRef.current.contains(event.target as Node)) {
                        setMobileMenuOpen(false);
                    }
                }}
            >
                <Collapse in={mobileMenuOpen} sx={{ 
                    display: { xs: 'block', md: 'none' },
                    position: 'absolute',
                    width: '100%',
                    top: '90px',
                    left: 0,
                    zIndex: 1000,
                }}>
                    <Box 
                        sx={{ 
                            width: '100%',
                            bgcolor: 'background.header',
                            borderBottom: '1px solid #1e1e1e',
                            py: 2,
                            px: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                        }}
                    >
                        <MenuItems />
                    </Box>
                </Collapse>
            </ClickAwayListener>
        </Box>
    );
};

export default Header;
